import { AdminFundraiserCard } from "@components/AdminFundraiserCard";
import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Card } from "@components/Card";
import { ErrorMessage } from "@components/ErrorMessage";
import { IconDisplay, IconSize, Icon } from "@components/Icon";
import { Loading } from "@components/LoadingIndicator";
import { MasonryList } from "@components/layout/MasonryList";
import { css } from "@emotion/react";
import { UUID } from "io-ts-types";
import { useState, useCallback } from "react";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import {
  getRoutePath,
  URLFormat,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";
import { FundraisersFullResponse } from "@every.org/common/src/routes/fundraiser";

import { SearchSelectorInput } from "src/components/SearchSelectorInput";
import { AutocompleteSection } from "src/components/SearchTextInput";
import { PageSection } from "src/components/layout/PageSection";
import { useLoggedInUser } from "src/context/AuthContext/hooks";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { useEdoRouter } from "src/hooks/useEdoRouter";
import {
  fetchUserFundraisers,
  UserFundraisersFetchStatus,
} from "src/pages/Fundraiser/fetchFundraiser";
import { ManagePageLayout, ManagePageTab } from "src/pages/Manage/index";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { FontWeight } from "src/theme/text";

const MAX_TO_FETCH = 150;

export const EmptyStateCard = () => {
  const { push: routerPush } = useEdoRouter();

  const [nonprofitSlugsToAdd, setNonprofitSlugsToAdd] = useState<
    string | undefined
  >();
  const [error, setError] = useState(false);

  const onNonprofitChange = (nonprofitId?: string) => {
    setNonprofitSlugsToAdd(nonprofitId);
    nonprofitId && setError(false);
  };

  const [loading, setLoading] = useState(false);

  return (
    <Card css={{ marginBottom: "150px" }}>
      <div css={[verticalStackCss.l, { maxWidth: "600px", margin: "0 auto" }]}>
        <Icon
          iconImport={() => import("@components/Icon/icons/RocketIcon")}
          display={IconDisplay.ACCENT}
          size={IconSize.XX_LARGE}
          css={{ margin: "0 auto" }}
        />
        <h3
          css={css`
            font-weight: ${FontWeight.REGULAR};
          `}
        >
          You haven’t created any fundraisers yet. You can start one from any
          nonprofit’s profile page, or by searching for a nonprofit below.
        </h3>
        <SearchSelectorInput
          selector={AutocompleteSection.NONPROFITS}
          onSelectedChange={(x) => {
            onNonprofitChange(x ? x[0] : undefined);
          }}
          data-tname="includeNonprofits"
          multiSelect={false}
        />
        {error && (
          <ErrorMessage text="Please choose a nonprofit for your fundraiser" />
        )}
        <Button
          data-tname="nonprofitAdmin-empty-createFundraiser"
          role={ButtonRole.PRIMARY}
          css={{ maxWidth: "220px", marginLeft: "auto", marginRight: "0" }}
          disabled={!nonprofitSlugsToAdd || loading}
          submitting={loading}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => {
              setLoading(true);
              const link =
                nonprofitSlugsToAdd &&
                getRoutePath({
                  name: ClientRouteName.FUNDRAISER_CREATE,
                  tokens: {
                    nonprofitSlug: nonprofitSlugsToAdd,
                  },
                  format: URLFormat.RELATIVE,
                });
              if (link) {
                setError(false);
                routerPush(link);
              } else {
                setLoading(false);
                setError(true);
              }
            },
          }}
        >
          Create fundraiser
        </Button>
      </div>
    </Card>
  );
};

const FundraisersPage = () => {
  const loggedInUser = useLoggedInUser();

  const [fundraisers, setFundraisers] = useState<
    FundraisersFullResponse | UserFundraisersFetchStatus
  >(UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS);
  const [listOfFundraisers, setListOfFundraisers] = useState<
    FundraiserResponse[] | UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS
  >(UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS);

  const asyncOperation = useCallback(
    () => fetchUserFundraisers(loggedInUser.id, MAX_TO_FETCH),
    [loggedInUser]
  );

  const handleResponse = useCallback(
    (fundraisers: FundraisersFullResponse | UserFundraisersFetchStatus) => {
      setFundraisers(fundraisers);
      if (
        fundraisers !== UserFundraisersFetchStatus.USER_FUNDRAISERS_NOT_FOUND &&
        fundraisers !== UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS
      ) {
        setListOfFundraisers(fundraisers.fundraisers);
      }
    },
    []
  );
  const handleError = useCallback(
    () => setFundraisers(UserFundraisersFetchStatus.USER_FUNDRAISERS_NOT_FOUND),
    []
  );

  useAsyncEffect({
    asyncOperation,
    handleResponse,
    handleError,
  });

  const onFundraiserDeleted = useCallback(
    (id: UUID) => {
      if (
        fundraisers !== UserFundraisersFetchStatus.USER_FUNDRAISERS_NOT_FOUND &&
        fundraisers !== UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS
      ) {
        setListOfFundraisers(() =>
          fundraisers.fundraisers.filter((elem) => elem.id !== id)
        );
      }
    },
    [fundraisers]
  );

  return (
    <ManagePageLayout
      pageTitle={"Fundraisers"}
      selectedTab={ManagePageTab.Fundraisers}
      pageContentCss={css`
        margin: ${spacing.xl} 0;
      `}
    >
      {listOfFundraisers ===
      UserFundraisersFetchStatus.FETCHING_USER_FUNDRAISERS ? (
        <Loading />
      ) : (
        <PageSection>
          {listOfFundraisers.length === 0 ? (
            <EmptyStateCard />
          ) : (
            <MasonryList
              items={listOfFundraisers.map((fundraiser) => {
                return {
                  key: fundraiser.id,
                  elem: (
                    <AdminFundraiserCard
                      fundraiser={fundraiser}
                      onFundraiserDeleted={onFundraiserDeleted}
                      userId={loggedInUser.id}
                    />
                  ),
                };
              })}
            />
          )}
        </PageSection>
      )}
    </ManagePageLayout>
  );
};

export default FundraisersPage;
